import { useEffect, useMemo, useState } from "react";
import { DataTable, Modal } from "@iq/models";
import useIsSmallScreen from "../../hooks/useIsSmallScreen";
import { getOrderHistory } from "../../services/OrderService";
import { fetchOrderReceipt } from "../../services/customerService";

function CustomerDetails({
    isOpen,
    customer,
    onClose,
}) {
    const isSmallScreen = useIsSmallScreen();
    const [orderHistory, setOrderHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (customer) {
            fetchOrderHistory(customer.id);
        }
    }, [customer]);

    const tableConfig = useMemo(() => ({
        columns: [
            {
                key: "id",
                label: "Order",
                accessor: "id",
                width: "1fr",
                formatter: (id) => `#${id}`,
                filterable: true,
            },
            {
                key: "status",
                label: "Status",
                accessor: "status",
                width: "3fr",
                filterable: true,
            },
            {
                key: "total_amount",
                label: "Total",
                accessor: "total_amount",
                width: "3fr",
                formatter: (amount) => formatCurrency(amount),
                filterable: true,
            },
            {
                key: "receipt",
                label: "Receipt",
                accessor: "id",
                width: "4fr",
                renderCell: ({ row }) => (
                    <div>
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                openReceipt(row.id);
                            }}
                            style={{
                                padding: "6px 10px",
                                borderRadius: "6px",
                                border: "1px solid #2d6cdf",
                                background: "#eaf1ff",
                                color: "#1d4ed8",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Open PDF
                        </button>
                    </div>
                ),
                filterable: false,
                searchable: false,
            },
        ],
        emptyLabel: "No order history found.",
        searchPlaceholder: "Search order history",
    }), []);

    const mobileTableConfig = useMemo(() => ({
        columns: [
            {
                key: "id",
                label: "Order",
                accessor: "id",
                width: "1.5fr",
                formatter: (id) => `#${id}`,
            },
            {
                key: "status",
                label: "Status",
                accessor: "status",
                width: "3fr",
                filterable: true,
            },
            {
                key: "total_amount",
                label: "Total",
                accessor: "total_amount",
                width: "3fr",
                formatter: (amount) => formatCurrency(amount),
            },
            {
                key: "receipt",
                label: "Receipt",
                accessor: "id",
                width: "4fr",
                renderCell: ({ row }) => (
                    <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                openReceipt(row.id);
                            }}
                            style={{
                                padding: "6px 10px",
                                borderRadius: "6px",
                                border: "1px solid #2d6cdf",
                                background: "#eaf1ff",
                                color: "#1d4ed8",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Open PDF
                        </button>
                    </div>
                ),
                filterable: false,
                searchable: false,
            },
        ],
        emptyLabel: "No order history found.",
        searchPlaceholder: "Search order history",
    }), []);

    async function fetchOrderHistory(customerId) {
        setIsLoading(true);
        setErrorMessage("");

        try {
            const customerWithHistory = await getOrderHistory(customerId);
            const history = Array.isArray(customerWithHistory?.orders)
                ? customerWithHistory.orders
                : [];

            setOrderHistory(history);
        } catch (error) {
            setErrorMessage(error.message || "Failed to load order history");
            setOrderHistory([]);
        } finally {
            setIsLoading(false);
        }
    }

    async function openReceipt(orderId) {
        try {
            const blob = await fetchOrderReceipt(orderId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => window.URL.revokeObjectURL(url), 0);
        } catch (error) {
            setErrorMessage(error.message || "Failed to load receipt");
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={customer ? `${customer.name}'s Order History` : "Customer Details"}
            wide={true}
        >
            {errorMessage && <p className="error">{errorMessage}</p>}
            {isLoading ? (
                <p>Loading order history...</p>
            ) : (
                <div>
                    <DataTable
                        rowData={orderHistory}
                        rowKey={(order) => order.id}
                        tableConfig={isSmallScreen ? mobileTableConfig : tableConfig}
                    />
                </div>
            )}
        </Modal>
    );
}

function formatCurrency(amount) {
    const cents = Number(amount);

    if (!Number.isFinite(cents)) {
        return "-";
    }

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(cents / 100);
}

export default CustomerDetails;